/**
 * AppPackagesAgGridHook
 *
 * Grid for /ota/packages. Uses AG Grid's server-side row model (see
 * mountServerSideGrid) for consistency with the other OTA/parameter grids,
 * even though today's package catalog is small — the underlying
 * `list_app_packages/1` query was unbounded, so this closes the same gap
 * before it becomes a problem.
 */

import { mountServerSideGrid } from "../base/ServerSideAgGridHook";

const PAGE_SIZE = 25;

const dateCell = (params) => {
  if (!params.value) return "";
  const [date, time] = String(params.value).split("T");
  return `<div>
    <div>${date || params.value}</div>
    <div style="font-size:12px;color:var(--ag-secondary-foreground-color);">${(time || "").split(".")[0]}</div>
  </div>`;
};

const AppPackagesAgGridHook = {
  mounted() {
    if (!window.agGrid || !window.agGrid.createGrid) {
      console.error("AppPackagesAgGridHook: agGrid not found on window");
      return;
    }

    const self = this;

    const actionsRenderer = (params) => {
      if (!params.data) return "";
      const { id, file_path } = params.data;
      const wrap = document.createElement("div");
      wrap.style.cssText = "display:flex;align-items:center;gap:10px;padding:2px 0;";

      const btn = (label, color, onClick) => {
        const el = document.createElement("button");
        el.textContent = label;
        el.style.cssText = `color:${color};font-size:12px;cursor:pointer;background:none;border:none;padding:0;white-space:nowrap;`;
        el.addEventListener("click", (e) => {
          e.stopPropagation();
          onClick();
        });
        return el;
      };

      wrap.appendChild(btn("Edit", "#2563EB", () => self.pushEvent("edit", { id: String(id) })));

      if (file_path) {
        const link = document.createElement("a");
        link.href = file_path;
        link.download = "";
        link.textContent = "Download";
        link.style.cssText = "color:#16A34A;font-size:12px;text-decoration:none;white-space:nowrap;";
        link.addEventListener("click", (e) => e.stopPropagation());
        wrap.appendChild(link);
      }

      wrap.appendChild(
        btn("Delete", "#DC2626", () => {
          if (window.confirm("Delete this package?")) {
            self.pushEvent("delete", { id: String(id) });
          }
        })
      );

      return wrap;
    };

    const columnDefs = [
      { headerName: "Version Name", field: "version_name", flex: 1.2, minWidth: 160 },
      { headerName: "Model", field: "model", width: 140 },
      { headerName: "Vendor", field: "vendor", width: 140 },
      {
        headerName: "Versions",
        colId: "versions",
        flex: 1,
        minWidth: 160,
        sortable: false,
        cellRenderer: (params) => {
          if (!params.data) return "";
          const { app_version, data_version, system_version } = params.data;
          return `<div style="font-size:12px;line-height:1.5;">
            <div>App: <b>${app_version || "—"}</b></div>
            <div>Data: <b>${data_version || "—"}</b></div>
            <div>Sys: <b>${system_version || "—"}</b></div>
          </div>`;
        },
      },
      { headerName: "Creator", field: "creator_id", width: 100 },
      { headerName: "Upload Time", field: "inserted_at", width: 160, cellRenderer: dateCell },
      {
        headerName: "Actions",
        colId: "actions",
        pinned: "right",
        width: 170,
        sortable: false,
        filter: false,
        floatingFilter: false,
        resizable: false,
        cellRenderer: actionsRenderer,
      },
    ];

    this.gridApi = mountServerSideGrid(this, {
      columnDefs,
      rowsEvent: "app_packages:rows",
      refreshEvent: "app_packages:refresh",
      pageSize: PAGE_SIZE,
    });
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default AppPackagesAgGridHook;
